import { useEffect, useState } from "react"

const useCameraPermission = () => {
  const [permissionState, setPermissionState] = useState("unknown")
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const hasPermissionsAPI = "permissions" in navigator && !isSafari

  // Check camera access by attempting to use it
  const checkCameraAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())
      return "granted"
    } catch (error) {
      return error.name === "NotAllowedError" ? "denied" : "prompt"
    }
  }

  useEffect(() => {
    let permissionStatus = null

    const initializePermissions = async () => {
      if (hasPermissionsAPI) {
        // Chrome/Edge: Use Permissions API
        try {
          return await checkCameraAccessThroughPermissionsAPI()
        } catch (error) {
          // Fallback if Permissions API fails
          await checkCameraAccessDirectly()
        }
      } else {
        // Safari: Check camera access directly
        await checkCameraAccessDirectly()
      }
    }

    const checkCameraAccessThroughPermissionsAPI = async () => {
      permissionStatus = await navigator.permissions.query({ name: "camera" })
      setPermissionState(permissionStatus.state)

      const handleChange = () => setPermissionState(permissionStatus.state)
      permissionStatus.addEventListener("change", handleChange)

      // return a cleanupFn
      return () => permissionStatus.removeEventListener("change", handleChange)
    }

    const checkCameraAccessDirectly = async () => {
      const state = await checkCameraAccess()
      setPermissionState(state)
    }

    const cleanup = initializePermissions()

    return () => {
      cleanup.then((cleanupFn) => cleanupFn?.())
    }
  }, [hasPermissionsAPI])

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())

      // Update state for Safari after successful request
      if (!hasPermissionsAPI) {
        setPermissionState("granted")
      }
    } catch (error) {
      // Update state for Safari on denial
      if (!hasPermissionsAPI && error.name === "NotAllowedError") {
        setPermissionState("denied")
      }
    }
  }

  return {
    permissionState,
    requestCameraPermission,
    canRequestPermission: () => permissionState !== "denied",
  }
}

export default useCameraPermission
